(() => {
  const index = window.LSP_FRAGRANCE_INDEX;
  if (!index?.records?.length) return;

  const encoder = new TextEncoder();
  const digestCache = new Map();
  let libraryPromise = null;

  function loadLibrary(url) {
    if (window.LSP_FRAGRANCE_LIBRARY) return Promise.resolve(window.LSP_FRAGRANCE_LIBRARY);
    if (!url) return Promise.resolve(null);
    if (libraryPromise) return libraryPromise;
    libraryPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => resolve(window.LSP_FRAGRANCE_LIBRARY || null);
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
    return libraryPromise;
  }

  function collapseSpacedLetters(value) {
    let output = value;
    let previous = '';
    const pattern = /\b(?:[a-z0-9]\s+){2,}[a-z0-9]\b/g;
    while (previous !== output) {
      previous = output;
      output = output.replace(pattern, (match) => match.replace(/\s+/g, ''));
    }
    return output;
  }

  function normalize(value = '') {
    const simplified = String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' y ')
      .replace(/n[º°]/g, 'numero ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return collapseSpacedLetters(simplified);
  }

  function grams(value, size = 3) {
    const padded = `  ${normalize(value)}  `;
    const result = new Set();
    for (let indexPosition = 0; indexPosition <= padded.length - size; indexPosition += 1) {
      result.add(padded.slice(indexPosition, indexPosition + size));
    }
    return [...result];
  }

  function tokens(value) {
    return [...new Set(normalize(value).split(' ').filter((token) => token.length > 1))];
  }

  async function digest(value) {
    if (digestCache.has(value)) return digestCache.get(value);
    const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(value));
    const bytes = new Uint8Array(buffer).slice(0, 9);
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    const output = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    digestCache.set(value, output);
    return output;
  }

  async function queryFingerprint(value) {
    const normalized = normalize(value);
    return {
      e: await digest(`e:${normalized}`),
      g: new Set(await Promise.all(grams(normalized).map((gram) => digest(`g:${gram}`)))),
      w: new Set(await Promise.all(tokens(normalized).map((token) => digest(`w:${token}`)))),
    };
  }

  async function libraryProfile(query, libraryUrl) {
    const library = await loadLibrary(libraryUrl);
    if (!library?.h || !library?.p) return null;
    const profileIndex = library.h[query.e];
    if (profileIndex === undefined) return null;
    const profile = library.p[profileIndex];
    if (!profile) return null;
    return {
      z: 'unisex',
      f: profile[0] || '',
      n: profile[1] || [],
    };
  }

  function scentHintProfile(value) {
    const terms = new Set(normalize(value).split(' '));
    const hints = [
      ['vainilla', 'ambarada vainilla', ['vainilla']],
      ['vanilla', 'ambarada vainilla', ['vainilla']],
      ['oud', 'amaderada oriental', ['oud', 'maderas']],
      ['rosa', 'floral', ['rosa']],
      ['rose', 'floral', ['rosa']],
      ['jazmin', 'floral', ['jazmin']],
      ['jasmine', 'floral', ['jazmin']],
      ['citrico', 'citrica fresca', ['limon', 'bergamota']],
      ['citrica', 'citrica fresca', ['limon', 'bergamota']],
      ['limon', 'citrica fresca', ['limon']],
      ['cuero', 'cuero amaderada', ['cuero']],
      ['tobacco', 'oriental tabaco', ['tabaco']],
      ['tabaco', 'oriental tabaco', ['tabaco']],
      ['coco', 'gourmand dulce', ['coco']],
      ['almizcle', 'almizclada', ['almizcle']],
      ['musk', 'almizclada', ['almizcle']],
    ];
    const match = hints.find(([term]) => terms.has(term));
    if (!match) return null;
    return { z: 'unisex', f: match[1], n: match[2] };
  }

  function setOverlap(left, right) {
    if (!left.size || !right.size) return 0;
    let overlap = 0;
    left.forEach((value) => { if (right.has(value)) overlap += 1; });
    return overlap;
  }

  function fingerprintScore(query, candidate) {
    if (query.e === candidate.e) return 1;
    const candidateGrams = new Set(candidate.g);
    const gramOverlap = setOverlap(query.g, candidateGrams);
    const gramScore = (2 * gramOverlap) / Math.max(1, query.g.size + candidateGrams.size);
    const candidateWords = new Set(candidate.w);
    const wordScore = setOverlap(query.w, candidateWords)
      / Math.max(1, Math.min(query.w.size, candidateWords.size));
    return (gramScore * 0.72) + (wordScore * 0.28);
  }

  function bestFingerprintScore(query, fingerprints) {
    return fingerprints.reduce((best, fingerprint) => (
      Math.max(best, fingerprintScore(query, fingerprint))
    ), 0);
  }

  function bestBrandScore(query, fingerprints) {
    return fingerprints.reduce((best, fingerprint) => {
      const candidateWords = new Set(fingerprint.w);
      const queryCoverage = setOverlap(query.w, candidateWords) / Math.max(1, query.w.size);
      if (queryCoverage < 0.66) return best;
      return Math.max(best, fingerprintScore(query, fingerprint));
    }, 0);
  }

  function plainTokenSimilarity(left, right) {
    const a = new Set(normalize(left).split(' ').filter(Boolean));
    const b = new Set(normalize(right).split(' ').filter(Boolean));
    if (!a.size || !b.size) return 0;
    const overlap = setOverlap(a, b);
    return overlap / (a.size + b.size - overlap);
  }

  function recommendationScore(profile, record) {
    if (!record.y || !record.f) return 0;
    const familyScore = plainTokenSimilarity(profile.f, record.f);
    const profileNotes = new Set(profile.n || []);
    const recordNotes = new Set(record.n || []);
    const noteScore = profileNotes.size
      ? setOverlap(profileNotes, recordNotes) / profileNotes.size
      : 0;
    const genderScore = profile.z === 'unisex' || record.z === 'unisex' || profile.z === record.z ? 1 : 0;
    return (familyScore * 0.55) + (noteScore * 0.35) + (genderScore * 0.10);
  }

  function productEndpoint(handle) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/products/${handle}.js`;
  }

  async function hydrateProduct(record) {
    try {
      const response = await fetch(productEndpoint(record.id), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      return {
        ...record,
        title: product.title,
        url: product.url || record.u,
        image: typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.src || product.images?.[0] || '',
        price: Number(product.price || 0) / 100,
        available: Boolean(product.available),
      };
    } catch (_error) {
      return {
        ...record,
        title: 'Ver perfume recomendado',
        url: record.u,
        image: '',
        price: 0,
        available: Boolean(record.a),
      };
    }
  }

  function formatMoney(amount, currency) {
    if (!amount) return '';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  }

  function createCard(product, currency) {
    const article = document.createElement('article');
    article.className = 'lsp-finder-card';

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'lsp-finder-card__image-link';

    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 480;
      image.height = 600;
      link.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'lsp-finder-card__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = 'LS';
      link.appendChild(placeholder);
    }

    const badge = document.createElement('span');
    badge.className = `lsp-finder-card__badge ${product.available ? 'is-available' : 'is-sold-out'}`;
    badge.textContent = product.available ? 'Disponible' : 'Agotado';
    link.appendChild(badge);
    article.appendChild(link);

    const body = document.createElement('div');
    body.className = 'lsp-finder-card__body';

    const title = document.createElement('h3');
    title.className = 'lsp-finder-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.textContent = product.title;
    title.appendChild(titleLink);
    body.appendChild(title);

    if (product.f) {
      const family = document.createElement('p');
      family.className = 'lsp-finder-card__family';
      family.textContent = product.f.replace(/\b\w/g, (letter) => letter.toUpperCase());
      body.appendChild(family);
    }

    const priceText = formatMoney(product.price, currency);
    if (priceText) {
      const price = document.createElement('p');
      price.className = 'lsp-finder-card__price';
      price.textContent = priceText;
      body.appendChild(price);
    }

    const button = document.createElement('a');
    button.href = product.url;
    button.className = 'lsp-finder-card__button';
    button.textContent = product.available ? 'Ver perfume' : 'Avísame cuando vuelva';
    body.appendChild(button);
    article.appendChild(body);
    return article;
  }

  function createSuggestion(product, currency, position) {
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'lsp-finder-suggestion';
    link.setAttribute('role', 'option');
    link.id = `LspFinderSuggestion-${position}`;

    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.className = 'lsp-finder-suggestion__image';
      image.loading = 'lazy';
      image.width = 72;
      image.height = 72;
      link.appendChild(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'lsp-finder-suggestion__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.textContent = 'LS';
      link.appendChild(placeholder);
    }

    const content = document.createElement('span');
    content.className = 'lsp-finder-suggestion__content';
    const title = document.createElement('span');
    title.className = 'lsp-finder-suggestion__title';
    title.textContent = product.title;
    content.appendChild(title);

    const meta = document.createElement('span');
    meta.className = 'lsp-finder-suggestion__meta';
    const price = formatMoney(product.price, currency);
    meta.textContent = product.available
      ? ['Disponible', price].filter(Boolean).join(' · ')
      : 'Agotado — entra para solicitar el aviso';
    content.appendChild(meta);
    link.appendChild(content);
    return link;
  }

  function trackSearch(term, resultType, resultCount) {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({
      event: 'perfume_finder_search',
      search_term: term,
      result_type: resultType,
      result_count: resultCount,
    });
  }

  async function search(rawQuery, limit, libraryUrl, options = {}) {
    const predictive = Boolean(options.predictive);
    const normalizedRaw = normalize(rawQuery);
    const wantsSamples = /\b(muestra|muestras|3ml|3 ml)\b/.test(normalizedRaw);
    const cleanedQuery = normalizedRaw
      .replace(/\b(muestra|muestras|3ml|3 ml)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const allowedRecords = index.records.filter((record) => (
      wantsSamples ? record.s : !record.s
    ));

    if (wantsSamples && !cleanedQuery) {
      const sampleRecords = allowedRecords
        .filter((record) => record.a)
        .slice(0, limit);
      return {
        kind: 'samples',
        primary: await Promise.all(sampleRecords.map(hydrateProduct)),
        recommendations: [],
      };
    }

    if (cleanedQuery.length < 2) {
      return { kind: 'short', primary: [], recommendations: [] };
    }

    const query = await queryFingerprint(cleanedQuery);
    const brandQueryText = cleanedQuery
      .replace(/\b(perfume|perfumes|marca|marcas|de|del)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || cleanedQuery;
    const brandQuery = brandQueryText === cleanedQuery
      ? query
      : await queryFingerprint(brandQueryText);
    const rankedRecords = allowedRecords
      .map((record) => {
        const exact = record.q.some((fingerprint) => fingerprint.e === query.e);
        const brandExact = (record.b || []).some((fingerprint) => fingerprint.e === brandQuery.e);
        const brandScore = bestBrandScore(brandQuery, record.b || []);
        const prefix = (record.x || []).includes(query.e);
        const fuzzyScore = bestFingerprintScore(query, record.q);
        return {
          record,
          exact,
          brandExact,
          brandScore,
          prefix,
          score: exact ? 1 : prefix ? 0.985 : fuzzyScore,
        };
      })
      .sort((left, right) => (
        right.score - left.score
        || (right.record.p || 0) - (left.record.p || 0)
      ));
    const rankedProfiles = index.profiles
      .map((profile) => ({ profile, score: bestFingerprintScore(query, profile.q) }))
      .sort((left, right) => right.score - left.score);

    const brandExact = rankedRecords
      .filter((entry) => entry.brandExact)
      .sort((left, right) => (right.record.p || 0) - (left.record.p || 0))
      .slice(0, 20);
    const exact = rankedRecords.filter((entry) => entry.exact).slice(0, limit);
    const topBrandScore = Math.max(...rankedRecords.map((entry) => entry.brandScore), 0);
    const fuzzyBrand = !brandExact.length && !exact.length && topBrandScore >= 0.72
      ? rankedRecords
        .filter((entry) => entry.brandScore >= Math.max(0.72, topBrandScore - 0.04))
        .sort((left, right) => (right.record.p || 0) - (left.record.p || 0))
        .slice(0, 20)
      : [];
    const prefix = rankedRecords
      .filter((entry) => !entry.exact && entry.prefix)
      .slice(0, Math.min(10, Math.max(limit, 10)));
    let primaryEntries = brandExact.length
      ? brandExact
      : exact.length
        ? exact
        : fuzzyBrand.length
          ? fuzzyBrand
          : prefix;
    let profile = primaryEntries.find((entry) => entry.record.f)?.record || null;
    let kind = brandExact.length || fuzzyBrand.length
      ? 'brand'
      : exact.length
        ? 'exact'
        : prefix.length
          ? 'predictive'
          : 'none';

    const allowFuzzySearch = !predictive || cleanedQuery.length >= 4;
    if (!primaryEntries.length && rankedProfiles[0]?.score === 1) {
      profile = rankedProfiles[0].profile;
      kind = 'profile';
    }

    if (!profile && !primaryEntries.length && (!predictive || cleanedQuery.length >= 4)) {
      profile = await libraryProfile(query, libraryUrl);
      if (profile) kind = 'profile';
    }

    if (!profile && !primaryEntries.length && allowFuzzySearch && rankedRecords[0]?.score >= 0.48) {
      const floor = Math.max(0.48, rankedRecords[0].score - 0.07);
      primaryEntries = rankedRecords.filter((entry) => entry.score >= floor).slice(0, Math.min(4, limit));
      profile = primaryEntries.find((entry) => entry.record.f)?.record || null;
      kind = 'fuzzy';
    }

    if (!profile && !primaryEntries.length && allowFuzzySearch && rankedProfiles[0]?.score >= 0.48) {
      profile = rankedProfiles[0].profile;
      kind = 'profile';
    }

    if (!profile && !primaryEntries.length) {
      profile = scentHintProfile(cleanedQuery);
      if (profile) kind = 'profile';
    }

    if (predictive && cleanedQuery.length < 4 && !primaryEntries.length) {
      return { kind: 'predictive-empty', primary: [], recommendations: [], basis: 'none' };
    }

    const primary = await Promise.all(primaryEntries.map((entry) => hydrateProduct(entry.record)));
    const shouldRecommend = Boolean(profile)
      && (kind === 'profile' || !primary.some((product) => product.available));
    let recommendations = [];
    let basis = 'none';

    if (shouldRecommend) {
      const primaryIds = new Set(primary.map((product) => product.id));
      const candidates = allowedRecords
        .filter((record) => record.a && record.y && !primaryIds.has(record.id))
        .map((record) => ({ record, score: recommendationScore(profile, record) }))
        .filter((entry) => entry.score >= 0.10)
        .sort((left, right) => (
          right.score - left.score
          || (right.record.p || 0) - (left.record.p || 0)
        ))
        .slice(0, limit * 3);
      const hydrated = await Promise.all(candidates.map((entry) => hydrateProduct(entry.record)));
      recommendations = hydrated.filter((product) => product.available).slice(0, limit);
      if (recommendations.length) basis = 'scent';
    }

    if (!primary.length && !profile) kind = 'fallback';

    if ((kind === 'fallback' || shouldRecommend) && recommendations.length < Math.min(3, limit)) {
      const usedIds = new Set([
        ...primary.map((product) => product.id),
        ...recommendations.map((product) => product.id),
      ]);
      const popularRecords = allowedRecords
        .filter((record) => record.a && record.y && !usedIds.has(record.id))
        .sort((left, right) => (right.p || 0) - (left.p || 0))
        .slice(0, limit * 3);
      const hydratedPopular = await Promise.all(popularRecords.map(hydrateProduct));
      const fillers = hydratedPopular
        .filter((product) => product.available && !usedIds.has(product.id))
        .slice(0, limit - recommendations.length);
      recommendations = [...recommendations, ...fillers];
      if (fillers.length) basis = basis === 'scent' ? 'mixed' : 'popular';
    }

    return { kind, primary, recommendations, basis };
  }

  function initFinder(root) {
    if (root.dataset.finderInitialized === 'true') return;
    const form = root.querySelector('[data-finder-form]');
    const input = root.querySelector('[data-finder-input]');
    const button = root.querySelector('[data-finder-submit]');
    const status = root.querySelector('[data-finder-status]');
    const results = root.querySelector('[data-finder-results]');
    const suggestions = root.querySelector('[data-finder-suggestions]');
    const keyboardHint = root.querySelector('[data-finder-keyboard-hint]');
    const resetButton = form.querySelector?.('button[type="reset"]') || null;
    if (!form || !input || !button || !status || !results || !suggestions) return;
    root.dataset.finderInitialized = 'true';
    const isHeaderFinder = root.hasAttribute
      ? root.hasAttribute('data-lsp-header-finder')
      : root.dataset.lspHeaderFinder !== undefined;
    const currency = root.dataset.currency || 'EUR';
    const limit = Number(root.dataset.limit || 6);
    const libraryUrl = root.dataset.libraryUrl || '';
    let requestNumber = 0;
    let predictiveTimer = null;
    let suggestionLinks = [];

    function updateKeyboardHint() {
      if (!keyboardHint) return;
      const isMobile = window.matchMedia?.('(max-width: 680px)').matches
        || (Number(window.innerWidth) > 0 && Number(window.innerWidth) <= 680);
      const viewport = window.visualViewport;
      const inputRect = input.getBoundingClientRect?.();
      const keyboardHeight = viewport ? Math.max(0, window.innerHeight - viewport.height) : 0;
      const roomBelow = viewport && inputRect
        ? viewport.offsetTop + viewport.height - inputRect.bottom
        : 0;
      const keyboardIsTight = Boolean(isMobile && !suggestions.hidden && (
        viewport ? keyboardHeight > 120 && roomBelow < 300 : true
      ));
      keyboardHint.hidden = !keyboardIsTight;
      if (keyboardIsTight) root.classList.add('is-keyboard-tight');
      else root.classList.remove('is-keyboard-tight');
    }

    function hideSuggestions(clear = false) {
      suggestions.hidden = true;
      if (keyboardHint) keyboardHint.hidden = true;
      root.classList.remove('is-keyboard-tight');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      suggestionLinks.forEach((link) => link.classList.remove('is-active'));
      if (clear) {
        suggestions.replaceChildren();
        suggestionLinks = [];
      }
    }

    function showSuggestionMessage(message) {
      suggestions.replaceChildren();
      suggestionLinks = [];
      const paragraph = document.createElement('p');
      paragraph.className = 'lsp-finder-suggestions__message';
      paragraph.textContent = message;
      suggestions.appendChild(paragraph);
      suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      updateKeyboardHint();
    }

    function renderSuggestions(response) {
      suggestions.replaceChildren();
      suggestionLinks = [];
      let message = '';
      let products = [];

      if (response.kind === 'brand') {
        message = 'Estas son las opciones que tenemos de esa marca.';
        products = response.primary;
      } else if (['exact', 'predictive', 'fuzzy', 'samples'].includes(response.kind)) {
        message = response.kind === 'samples'
          ? 'Muestras disponibles'
          : 'Sugerencias mientras escribes';
        products = response.primary;
      } else if (response.kind === 'profile') {
        message = response.basis === 'scent'
          ? 'No tenemos ese perfume; estas alternativas comparten su perfil olfativo.'
          : 'No tenemos ese perfume; mira estas alternativas disponibles.';
        products = response.recommendations;
      } else if (response.kind === 'fallback') {
        message = 'No tenemos ese perfume; mira estas alternativas disponibles.';
        products = response.recommendations;
      }

      if (!products.length) {
        hideSuggestions(true);
        return;
      }

      const paragraph = document.createElement('p');
      paragraph.className = 'lsp-finder-suggestions__message';
      paragraph.textContent = message;
      suggestions.appendChild(paragraph);
      products.forEach((product, position) => {
        const suggestion = createSuggestion(product, currency, position);
        suggestionLinks.push(suggestion);
        suggestions.appendChild(suggestion);
      });
      suggestions.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      updateKeyboardHint();
    }

    function clearResults() {
      results.replaceChildren();
      results.hidden = true;
    }

    function renderGroup(titleText, products) {
      if (!products.length) return;
      const section = document.createElement('section');
      section.className = 'lsp-finder-results__group';
      const heading = document.createElement('h3');
      heading.className = 'lsp-finder-results__heading';
      heading.textContent = titleText;
      section.appendChild(heading);
      const grid = document.createElement('div');
      grid.className = 'lsp-finder-results__grid';
      products.forEach((product) => grid.appendChild(createCard(product, currency)));
      section.appendChild(grid);
      results.appendChild(section);
    }

    async function performSearch(rawQuery, options = {}) {
      const predictive = Boolean(options.predictive);
      if (!rawQuery) {
        clearResults();
        hideSuggestions(true);
        status.textContent = predictive ? '' : 'Escribe el perfume que quieres encontrar.';
        if (!predictive) input.focus();
        return;
      }

      const currentRequest = ++requestNumber;
      if (!predictive) button.disabled = true;
      root.classList.add('is-loading');
      if (predictive) {
        status.textContent = '';
        showSuggestionMessage('Buscando sugerencias…');
      } else {
        status.textContent = 'Buscando la mejor opción para ti…';
        hideSuggestions(true);
        clearResults();
      }

      try {
        const response = await search(rawQuery, limit, libraryUrl, { predictive });
        if (currentRequest !== requestNumber) return;

        if (predictive) {
          renderSuggestions(response);
          return;
        }

        results.replaceChildren();

        if (response.kind === 'short') {
          status.textContent = predictive ? '' : 'Escribe al menos dos letras.';
        } else if (response.kind === 'predictive-empty') {
          status.textContent = '';
        } else if (response.kind === 'samples') {
          status.textContent = 'Estas son las muestras disponibles ahora mismo.';
          renderGroup('Muestras disponibles', response.primary);
        } else if (response.kind === 'exact') {
          const available = response.primary.filter((product) => product.available);
          const soldOut = response.primary.filter((product) => !product.available);
          status.textContent = available.length
            ? 'Hemos encontrado opciones para tu búsqueda.'
            : 'Ese perfume está agotado ahora mismo. Entra en el producto para solicitar el aviso.';
          renderGroup('Coincidencias', available);
          renderGroup('Agotado — solicita el aviso', soldOut);
          renderGroup('También te pueden gustar', response.recommendations);
        } else if (response.kind === 'brand') {
          status.textContent = 'Estas son las opciones que tenemos asociadas a esa marca.';
          renderGroup('Resultados por marca', response.primary);
        } else if (response.kind === 'predictive') {
          status.textContent = 'Estas son las coincidencias más probables.';
          renderGroup('¿Buscabas alguno de estos?', response.primary);
        } else if (response.kind === 'fuzzy') {
          status.textContent = 'Hemos encontrado estas opciones parecidas a lo que has escrito.';
          renderGroup('¿Buscabas alguno de estos?', response.primary);
          renderGroup('También te pueden gustar', response.recommendations);
        } else if (response.kind === 'profile') {
          status.textContent = response.basis === 'scent'
            ? 'No tenemos exactamente ese perfume, pero estas opciones comparten su familia o varias notas olfativas.'
            : response.basis === 'mixed'
              ? 'No tenemos exactamente ese perfume. Te mostramos las alternativas más parecidas y otras opciones disponibles.'
              : 'No tenemos exactamente ese perfume. Estas alternativas disponibles suelen gustar mucho.';
          renderGroup('Te pueden gustar', response.recommendations);
        } else if (response.kind === 'fallback') {
          status.textContent = 'No tenemos exactamente ese perfume. Estas alternativas disponibles suelen gustar mucho.';
          renderGroup('Alternativas disponibles', response.recommendations);
        } else {
          status.textContent = 'No tenemos exactamente ese perfume. Prueba también con el nombre de la marca.';
        }

        results.hidden = !results.children.length;
        if (!predictive) {
          trackSearch(rawQuery, response.kind, response.primary.length + response.recommendations.length);
          if (!results.hidden && typeof results.scrollIntoView === 'function') {
            const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            results.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
          }
        }
      } catch (_error) {
        if (predictive) {
          hideSuggestions(true);
          status.textContent = '';
        } else {
          status.textContent = 'No hemos podido completar la búsqueda. Inténtalo de nuevo.';
        }
      } finally {
        if (currentRequest === requestNumber) {
          if (!predictive) button.disabled = false;
          root.classList.remove('is-loading');
        }
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearTimeout(predictiveTimer);
      hideSuggestions(true);
      await performSearch(input.value.trim(), { predictive: isHeaderFinder });
    });

    input.addEventListener('input', () => {
      clearTimeout(predictiveTimer);
      button.disabled = false;
      resetButton?.classList.toggle('hidden', !input.value);
      const rawQuery = input.value.trim();
      if (normalize(rawQuery).length < 2) {
        requestNumber += 1;
        status.textContent = '';
        clearResults();
        hideSuggestions(true);
        root.classList.remove('is-loading');
        return;
      }
      clearResults();
      status.textContent = '';
      showSuggestionMessage('Buscando sugerencias…');
      predictiveTimer = setTimeout(() => {
        performSearch(rawQuery, { predictive: true });
      }, 220);
    });

    form.addEventListener('reset', () => {
      window.setTimeout(() => {
        requestNumber += 1;
        clearTimeout(predictiveTimer);
        clearResults();
        hideSuggestions(true);
        status.textContent = '';
        resetButton?.classList.add('hidden');
        input.focus();
      }, 0);
    });

    input.addEventListener('focus', () => {
      if (suggestionLinks.length) {
        suggestions.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        updateKeyboardHint();
      }
    });

    input.addEventListener('blur', () => {
      setTimeout(() => hideSuggestions(false), 180);
    });

    window.visualViewport?.addEventListener('resize', updateKeyboardHint);
    window.visualViewport?.addEventListener('scroll', updateKeyboardHint);

    const initialQuery = input.value.trim();
    if (normalize(initialQuery).length >= 2) {
      showSuggestionMessage('Buscando sugerencias…');
      performSearch(initialQuery, { predictive: true });
    }
  }

  function initAll(scope = document) {
    const roots = scope.matches?.('[data-lsp-fragrance-finder]')
      ? [scope]
      : scope.querySelectorAll?.('[data-lsp-fragrance-finder]') || [];
    roots.forEach(initFinder);
  }

  window.LSPFragranceFinder = { initAll };
  initAll(document);
})();
